import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  ChevronDown, ChevronUp, FileSearch, Pencil, Check, X, Undo2, AlertTriangle, Lock,
} from "lucide-react";

const fmtMoney = (n, cur = "KES") => {
  const v = Number(n);
  if (n == null || !Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  const c = cur === "USD" ? "$" : `${cur} `;
  if (a >= 1e9) return `${sign}${c}${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${sign}${c}${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e3) return `${sign}${c}${(a / 1e3).toFixed(0)}K`;
  return `${sign}${c}${a.toFixed(0)}`;
};

function FieldValue({ f, currency }) {
  if (f.not_found || f.value == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
        <AlertTriangle className="h-3 w-3" /> Not found in document
      </span>
    );
  }
  if (f.kind === "money") {
    return (
      <span className="text-sm font-extrabold text-neutral-900">
        {fmtMoney(f.value, currency)}
        {f.value_kes != null && currency !== "KES" && (
          <span className="ml-1 text-[11px] font-semibold text-neutral-400">
            ≈ {fmtMoney(f.value_kes, "KES")}
          </span>
        )}
      </span>
    );
  }
  if (f.kind === "pct") return <span className="text-sm font-extrabold text-neutral-900">{Number(f.value).toFixed(1)}%</span>;
  if (f.kind === "ratio") return <span className="text-sm font-extrabold text-neutral-900">{Number(f.value).toFixed(2)}×</span>;
  if (f.kind === "number") return <span className="text-sm font-extrabold text-neutral-900">{f.value}</span>;
  if (f.kind === "flag") {
    const good = ["CONSISTENT", "FULL", "COMPLETE", "STRONG", "SOURCED", "CLEAN", "DIVERSIFIED"].includes(f.value);
    const bad = ["VARIABLE", "SHORT", "MINIMAL", "SOLO", "ABSENT", "IMPAIRED", "HIGH", "BASIC"].includes(f.value);
    return (
      <span className={"inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
        (good ? "bg-emerald-100 text-emerald-800" : bad ? "bg-rose-100 text-rose-700" : "bg-neutral-200 text-neutral-700")}>
        {String(f.value).replaceAll("_", " ")}
      </span>
    );
  }
  return <span className="text-sm font-semibold text-neutral-800">{String(f.value)}</span>;
}

export function FiguresPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // "dt.field"
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/founder/figures").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data?.has_figures) return null;
  const okDocs = data.doc_types.filter((d) => d.status === "OK");
  const overriddenCount = okDocs.reduce(
    (n, d) => n + d.fields.filter((f) => f.overridden).length, 0);

  const saveOverride = async (dt, field, value) => {
    setSaving(true);
    try {
      const res = await api.post("/founder/figures/override", { doc_type: dt, field, value });
      setData(res.data);
      setEditing(null);
      toast.success(value == null
        ? "Correction removed — back to the extracted figure."
        : res.data?.capital_range?.locked
          ? "Figure corrected."
          : "Figure corrected — your Capital Range has been updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the correction.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="figures-panel"
      className="mt-6 rounded-2xl border border-neutral-200 bg-white">
      <button type="button" data-testid="figures-panel-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 p-5 text-left">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00828F]/10">
            <FileSearch className="h-5 w-5 text-[#00828F]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-neutral-900">
              Figures Clinq read from your documents
            </h3>
            <p className="text-xs text-neutral-500">
              {okDocs.length} document{okDocs.length === 1 ? "" : "s"} read
              {overriddenCount > 0 ? ` · ${overriddenCount} corrected by you` : ""} — spot an
              error? Correct it here{data.capital_range?.locked
                ? "." : " and your Capital Range updates instantly."}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 shrink-0 text-neutral-400" />
              : <ChevronDown className="h-5 w-5 shrink-0 text-neutral-400" />}
      </button>

      {open && (
        <div className="space-y-4 border-t border-neutral-100 p-5">
          {data.capital_range?.locked && data.capital_range?.teaser && (
            <div data-testid="capital-range-teaser"
              className="relative overflow-hidden rounded-xl border border-[#0A8B85]/25 bg-[#E0F3F5] p-5">
              <div className="select-none blur-[6px]" aria-hidden="true">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#066667]">
                  Recommended raise range
                </p>
                <p className="mt-1 text-3xl font-extrabold text-neutral-900">KES 8.4M – KES 21M</p>
                <p className="mt-1 text-xs text-neutral-600">UPPER tier · based on your documents</p>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/45 px-4 text-center">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#00303F]">
                  <Lock className="h-4 w-4" /> Your Recommended Capital Range is ready
                </span>
                <Link to="/founder/readiness-report"
                  data-testid="capital-range-teaser-unlock-btn"
                  className="rounded-full bg-[#00828F] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-[#00676F]">
                  Unlock full report
                </Link>
              </div>
            </div>
          )}
          {data.doc_types.map((d) => (
            <div key={d.doc_type} data-testid={`figures-doc-${d.doc_type}`}
              className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-neutral-900">{d.label}</p>
                <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                  (d.status === "OK" ? "bg-teal-100 text-teal-800" : "bg-rose-100 text-rose-700")}>
                  {d.status === "OK" ? `Read OK · ${d.currency}` : "Unreadable"}
                </span>
              </div>
              {d.status !== "OK" && d.prompt && (
                <p className="mt-2 text-xs text-rose-700">{d.prompt}</p>
              )}
              {d.status === "OK" && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {d.fields.map((f) => {
                    const key = `${d.doc_type}.${f.field}`;
                    const isEditing = editing === key;
                    return (
                      <div key={f.field} data-testid={`figure-field-${d.doc_type}-${f.field}`}
                        className={"rounded-lg border bg-white p-3 " +
                          (f.overridden ? "border-[#00828F]/50" : "border-neutral-200")}>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
                          {f.label}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                          {isEditing ? (
                            <div className="flex w-full items-center gap-1.5">
                              <input type="number" autoFocus value={editVal}
                                data-testid="figure-edit-input"
                                onChange={(e) => setEditVal(e.target.value)}
                                placeholder={`Correct value (${d.currency})`}
                                className="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm focus:border-[#00828F] focus:outline-none" />
                              <button type="button" disabled={saving || editVal === ""}
                                data-testid="figure-edit-save"
                                onClick={() => saveOverride(d.doc_type, f.field, Number(editVal))}
                                className="rounded-md bg-[#00828F] p-1.5 text-white disabled:opacity-40">
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" data-testid="figure-edit-cancel"
                                onClick={() => setEditing(null)}
                                className="rounded-md border border-neutral-300 p-1.5 text-neutral-500">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <FieldValue f={f} currency={d.currency} />
                              {f.editable && !f.overridden && (
                                <button type="button"
                                  data-testid={`figure-edit-${d.doc_type}-${f.field}`}
                                  onClick={() => { setEditing(key); setEditVal(f.value ?? ""); }}
                                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-neutral-400 transition hover:text-[#00828F]">
                                  <Pencil className="h-3 w-3" /> This is wrong
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {f.overridden && !isEditing && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-[#00828F]/10 px-2 py-0.5 text-[10px] font-bold text-[#00676F]">
                              Corrected by you
                              {f.original != null && f.original !== "NOT_FOUND"
                                ? ` · was ${f.kind === "money" ? fmtMoney(f.original, d.currency) : f.original}` : ""}
                            </span>
                            <button type="button"
                              data-testid={`figure-undo-${d.doc_type}-${f.field}`}
                              onClick={() => saveOverride(d.doc_type, f.field, null)}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-neutral-400 hover:text-rose-600">
                              <Undo2 className="h-3 w-3" /> Undo
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] italic text-neutral-400">
            {data.capital_range?.locked
              ? "Corrections are saved to your profile. Get your Capital Readiness Report to see the Recommended Capital Range (Section 7B) they feed into."
              : "Corrections are used in your Recommended Capital Range (Section 7B) and stay in place until you undo them or upload a newer version of the document."}
          </p>
        </div>
      )}
    </section>
  );
}
